import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject } from 'rxjs';
import { GuestVerificationCheckType } from '../../../../../models/Guest';
import { GuestDetails } from '../../../../../models/GuestDetails';
import { GuestVerificationResult } from '../../../../../models/GuestVerificationResult';
import { GuestVerificationInfo } from '../../../../../models/verification-info/GuestVerificationInfo';

@Component({
  selector: 'app-update-guest-verification-results-dialog',
  templateUrl: './update-guest-verification-results-dialog.component.html',
  styleUrls: ['./update-guest-verification-results-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCardModule,
    MatSelectModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule
  ]
})
export class UpdateGuestVerificationResultsDialogComponent implements OnInit {

  guestVerificationResults: any[] = [
    { check_code: '1001', status_text: 'Passed' },
    { check_code: '2001', status_text: 'Error' },
    { check_code: '3001', status_text: 'Consider' },
    { check_code: '4001', status_text: 'Processing' },
    { check_code: '5001', status_text: 'No Verification' }
  ]

  @Input() availableCheckTypes!: BehaviorSubject<GuestVerificationCheckType[]>;

  form!: FormGroup;
  guestDetails!: GuestDetails;
  guestVerificationInfo!: GuestVerificationInfo;
  guestVerificationCheckType = GuestVerificationCheckType;

  constructor(
    public dialogRef: MatDialogRef<UpdateGuestVerificationResultsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.initForm();

    if (this.data) {
      this.availableCheckTypes = this.data.availableCheckTypes;
      this.guestDetails = this.data.guestDetails;
      this.guestVerificationInfo = this.data.guestVerificationInfo;
      this.populateVerificationResultsForm(this.guestDetails);
    }
  }

  initForm() {
    this.form = this.formBuilder.group({
      identity_result: [''],
      identity_note: [''],
      document_result: [''],
      document_note: [''],
      watchlist_result: [''],
      watchlist_note: [''],
      sex_offender_result: [''],
      sex_offender_note: [''],
      criminal_result: [''],
      criminal_note: [''],
      bad_guest_result: [''],
      bad_guest_note: [''],
      fraud_result: [''],
      fraud_note: ['']
    });
  }

  populateVerificationResultsForm(guestDetails: GuestDetails) {
    guestDetails.verification_result_details.forEach(verifResult => {
      switch(verifResult.check_type) {
        case 'BAD GUEST':
          this.form.get('bad_guest_note')?.setValue(verifResult.note);
          break;
        case 'NATIONAL CRIMINAL':
          this.form.get('criminal_note')?.setValue(verifResult.note);
          break;
        case 'WATCHLIST':
          this.form.get('watchlist_note')?.setValue(verifResult.note);
          break;
        case 'SEX OFFENDER':
          this.form.get('sex_offender_note')?.setValue(verifResult.note);
          break;
        case 'IDENTITY':
          this.form.get('identity_note')?.setValue(verifResult.note);
          break;
        case 'DOCUMENT':
          this.form.get('document_note')?.setValue(verifResult.note);
          break;
        case 'FRAUD':
          this.form.get('fraud_note')?.setValue(verifResult.note);
          break;
      }
    });
  }

  getVerificationResultColor(result?: GuestVerificationResult) {
    if (result && result.color && result.text) {
      return result.color
    }
    return 'white';
  }

  submitGuestVerificationResultDetail() {
    let identity_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "IDENTITY");
    let document_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "DOCUMENT");
    let watchlist_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "WATCHLIST");
    let bad_guest_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "BAD GUEST");
    let sex_offender_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "SEX OFFENDER");
    let criminal_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "NATIONAL CRIMINAL");
    let fraud_result = this.guestDetails.verification_result_details.find(vr => vr.check_type === "FRAUD");

    let new_guest_verification_results = [];
    if (identity_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: identity_result.id,
        check_type_code: "10001",
        check_result_code: this.form.get('identity_result')?.value ? this.form.get('identity_result')?.value : identity_result.check_result_code,
        note: this.form.get('identity_note')?.value ? this.form.get('identity_note')?.value : identity_result.note
      });
    }
    if (document_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: document_result.id,
        check_type_code: "30001",
        check_result_code: this.form.get('document_result')?.value ? this.form.get('document_result')?.value : document_result.check_result_code,
        note: this.form.get('document_note')?.value ? this.form.get('document_note')?.value : document_result.note
      });
    }
    if (watchlist_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: watchlist_result.id,
        check_type_code: "20001",
        check_result_code: this.form.get('watchlist_result')?.value ? this.form.get('watchlist_result')?.value : watchlist_result.check_result_code,
        note: this.form.get('watchlist_note')?.value ? this.form.get('watchlist_note')?.value : watchlist_result.note
      });
    }
    if (bad_guest_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: bad_guest_result.id,
        check_type_code: "20002",
        check_result_code: this.form.get('bad_guest_result')?.value ? this.form.get('bad_guest_result')?.value : bad_guest_result.check_result_code,
        note: this.form.get('bad_guest_note')?.value ? this.form.get('bad_guest_note')?.value : bad_guest_result.note
      });
    }
    if (sex_offender_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: sex_offender_result.id,
        check_type_code: "40001",
        check_result_code: this.form.get('sex_offender_result')?.value ? this.form.get('sex_offender_result')?.value : sex_offender_result.check_result_code,
        note: this.form.get('sex_offender_note')?.value ? this.form.get('sex_offender_note')?.value : sex_offender_result.note
      });
    }
    if (criminal_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: criminal_result.id,
        check_type_code: "50001",
        check_result_code: this.form.get('criminal_result')?.value ? this.form.get('criminal_result')?.value : criminal_result.check_result_code,
        note: this.form.get('criminal_note')?.value ? this.form.get('criminal_note')?.value : criminal_result.note
      });
    }
    if (fraud_result?.id) {
      new_guest_verification_results.push({
        ss_check_id: fraud_result.id,
        check_type_code: "60002",
        check_result_code: this.form.get('fraud_result')?.value ? this.form.get('fraud_result')?.value : fraud_result.check_result_code,
        note: this.form.get('fraud_note')?.value ? this.form.get('fraud_note')?.value : fraud_result.note
      });
    }

    let payload = {
      guest_id: this.guestDetails.guest_id,
      reservation_guest_id: this.guestDetails.reservation_guest_details[0].id,
      new_guest_verification_results: new_guest_verification_results
    }
    this.dialogRef.close(payload);
  }

  shouldDisplayCheckType(checkType: GuestVerificationCheckType): boolean {
    return this.availableCheckTypes.value.includes(checkType);
  }

  shouldDisplayScreeningResultColumns(): boolean {
    return this.availableCheckTypes != null && this.availableCheckTypes.value.length > 0;
  }
}